import { useState, useCallback, useRef } from "react";
import { useFileUpload } from "@/hooks/useFileUpload";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, X, File, Image, FileText, Film, Music } from "lucide-react";
import { cn } from "@/lib/utils";

interface AttachmentUploaderProps {
  recordId: string;
  fieldId: string;
  onUploadComplete: (attachments: { filename: string; url: string; mime_type: string; size: number }[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  accept?: string;
  compact?: boolean;
}

export const AttachmentUploader = ({
  recordId,
  fieldId,
  onUploadComplete,
  maxFiles = 10,
  maxSizeMB = 10,
  accept = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx",
  compact = false,
}: AttachmentUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { uploads, isUploading, uploadFiles } = useFileUpload({
    maxSizeMB,
    onUploadComplete: (results) => {
      const successful = results
        .filter((r) => r.status === "complete" && r.url)
        .map((r) => ({
          filename: r.fileName,
          url: r.url!,
          mime_type: getMimeType(r.fileName),
          size: 0, // Size will be determined from file
        }));
      onUploadComplete(successful);
    },
  });

  const getMimeType = (filename: string): string => {
    const ext = filename.split(".").pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      mp4: "video/mp4",
      mp3: "audio/mpeg",
    };
    return mimeTypes[ext || ""] || "application/octet-stream";
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files).slice(0, maxFiles);
      if (files.length > 0) {
        await uploadFiles(files, recordId, fieldId);
      }
    },
    [maxFiles, recordId, fieldId, uploadFiles]
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, maxFiles);
    if (files.length > 0) {
      await uploadFiles(files, recordId, fieldId);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext || "")) {
      return <Image className="h-4 w-4" />;
    }
    if (["mp4", "mov", "avi", "webm"].includes(ext || "")) {
      return <Film className="h-4 w-4" />;
    }
    if (["mp3", "wav", "ogg", "m4a"].includes(ext || "")) {
      return <Music className="h-4 w-4" />;
    }
    if (["pdf"].includes(ext || "")) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="gap-1"
        >
          <Upload className="h-4 w-4" />
          {isUploading ? "Uploading..." : "Add"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50",
          isUploading && "opacity-50 pointer-events-none"
        )}
      >
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground mb-2">
          Drag files here or{" "}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-primary hover:underline"
          >
            browse
          </button>
        </p>
        <p className="text-xs text-muted-foreground">
          Max {maxSizeMB}MB per file • Up to {maxFiles} files
        </p>
      </div>

      {/* Upload progress */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          {uploads.map((upload, index) => (
            <div
              key={index}
              className={cn(
                "flex items-center gap-3 p-2 rounded-lg",
                upload.status === "error"
                  ? "bg-destructive/10"
                  : upload.status === "complete"
                  ? "bg-emerald-500/10"
                  : "bg-muted"
              )}
            >
              {getFileIcon(upload.fileName)}
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{upload.fileName}</p>
                {upload.status === "uploading" && (
                  <Progress value={upload.progress} className="h-1 mt-1" />
                )}
                {upload.status === "error" && (
                  <p className="text-xs text-destructive">{upload.error}</p>
                )}
              </div>
              {upload.status === "complete" && (
                <span className="text-xs text-emerald-600">✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
